import { allocateBoundedBudgetUnits } from "../budget-allocation";
import type { CampaignCreationCapabilities } from "../schemas/campaign-creation";
import type {
  SmartCreationReviewCampaign,
  SmartCreationReviewChange,
  SmartCreationReviewGraph,
  SmartCreationReviewWorkspace,
} from "../schemas/smart-creation-review";
import { SmartCreationReviewError } from "./errors";
import { requireCampaign, requireTarget, userValue } from "./graph";
import { validateNumeric } from "./validation";

export function redistributeBudget(
  graph: SmartCreationReviewGraph,
  selected: SmartCreationReviewCampaign,
  desired: number,
  capabilities: CampaignCreationCapabilities,
): void {
  const included = graph.campaigns.filter(
    (campaign) =>
      !campaign.excluded.effective && campaign.ref.lineageKey !== selected.ref.lineageKey,
  );
  const scale = 10 ** capabilities.dailyBudget.decimals;
  const deltaUnits =
    Math.round(desired * scale) - Math.round(selected.dailyBudget.effective * scale);
  if (included.length === 0 && deltaUnits !== 0) {
    throw new SmartCreationReviewError(
      422,
      "smart_creation.review_budget_invalid",
      "A single included Campaign must retain the Plan total daily budget",
    );
  }
  const originalUnits = included.map((campaign) =>
    Math.round(campaign.dailyBudget.effective * scale),
  );
  const otherTotalUnits = originalUnits.reduce((total, units) => total + units, 0);
  let allocatedDeltaUnits = 0;
  const proposedUnits = included.map((_, index) => {
    const previousUnits = originalUnits[index] ?? 0;
    const adjustmentUnits =
      index === included.length - 1
        ? deltaUnits - allocatedDeltaUnits
        : Math.round(
            deltaUnits *
              (otherTotalUnits > 0 ? previousUnits / otherTotalUnits : 1 / included.length),
          );
    allocatedDeltaUnits += adjustmentUnits;
    return previousUnits - adjustmentUnits;
  });
  const minimumUnits = Math.ceil(capabilities.dailyBudget.min * scale);
  const maximumUnits = Math.floor(capabilities.dailyBudget.max * scale);
  const nextUnits = proposedUnits.every((units) => units >= minimumUnits && units <= maximumUnits)
    ? proposedUnits
    : allocateBoundedBudgetUnits(
        originalUnits,
        otherTotalUnits - deltaUnits,
        minimumUnits,
        maximumUnits,
      );
  if (!nextUnits) {
    throw new SmartCreationReviewError(
      422,
      "smart_creation.review_budget_invalid",
      "Budget redistribution would place another Campaign outside marketplace limits",
    );
  }
  for (const [index, campaign] of included.entries()) {
    // Use the original weights and assign the final minor-unit remainder once;
    // reading already-mutated recipients here would silently change the Plan total.
    const next = (nextUnits[index] ?? 0) / scale;
    campaign.dailyBudget = userValue(campaign.dailyBudget, next);
    campaign.budgetAllocation = userValue(campaign.budgetAllocation, {
      basis: "amount",
      amount: next,
    });
  }
  selected.dailyBudget = userValue(selected.dailyBudget, desired);
}

export function redistributeBudgetForCampaignInclusion(
  graph: SmartCreationReviewGraph,
  selected: SmartCreationReviewCampaign,
  excluded: boolean,
  capabilities: CampaignCreationCapabilities,
): void {
  if (selected.excluded.effective === excluded) return;
  const currentlyIncluded = graph.campaigns.filter((campaign) => !campaign.excluded.effective);
  const recipients = currentlyIncluded.filter(
    (campaign) => campaign.ref.lineageKey !== selected.ref.lineageKey,
  );
  if (recipients.length === 0) {
    throw new SmartCreationReviewError(
      422,
      "smart_creation.review_all_campaigns_excluded",
      "At least one Campaign must remain included",
    );
  }
  const scale = 10 ** capabilities.dailyBudget.decimals;
  const planTotalUnits = currentlyIncluded.reduce(
    (total, campaign) => total + Math.round(campaign.dailyBudget.effective * scale),
    0,
  );
  const selectedUnits = Math.round(selected.dailyBudget.effective * scale);
  const targetUnits = excluded ? planTotalUnits : planTotalUnits - selectedUnits;
  const minimumUnits = Math.ceil(capabilities.dailyBudget.min * scale);
  const maximumUnits = Math.floor(capabilities.dailyBudget.max * scale);
  const allocated = allocateBoundedBudgetUnits(
    recipients.map((campaign) => Math.round(campaign.dailyBudget.effective * scale)),
    targetUnits,
    minimumUnits,
    maximumUnits,
  );
  if (!allocated) {
    throw new SmartCreationReviewError(
      422,
      "smart_creation.review_budget_invalid",
      "The total daily budget cannot fit the remaining included Campaigns",
    );
  }
  for (const [index, campaign] of recipients.entries()) {
    const amount = allocated[index]! / scale;
    campaign.dailyBudget = userValue(campaign.dailyBudget, amount);
    campaign.budgetAllocation = userValue(campaign.budgetAllocation, {
      basis: "amount",
      amount,
    });
  }
  selected.excluded = userValue(selected.excluded, excluded);
}

export function applyBudgetAllocations(
  graph: SmartCreationReviewGraph,
  change: Extract<SmartCreationReviewChange, { operation: "set_campaign_budget_allocations" }>,
  capabilities: CampaignCreationCapabilities,
): void {
  const included = graph.campaigns.filter((campaign) => !campaign.excluded.effective);
  const invalid = (message: string): never => {
    throw new SmartCreationReviewError(422, "smart_creation.review_budget_invalid", message);
  };
  const byCampaign = new Map(change.allocations.map((item) => [item.ref.lineageKey, item]));
  if (
    byCampaign.size !== included.length ||
    change.allocations.length !== included.length ||
    included.some((campaign) => !byCampaign.has(campaign.ref.lineageKey))
  ) {
    invalid("A budget allocation must cover every included Campaign exactly once");
  }
  for (const item of change.allocations) {
    if (requireCampaign(graph, item.ref).excluded.effective)
      invalid("An excluded Campaign cannot receive a budget allocation");
  }
  if (new Set(change.allocations.map((item) => item.allocation.basis)).size !== 1) {
    invalid("A budget allocation must use either amounts or percentages consistently");
  }
  const scale = 10 ** capabilities.dailyBudget.decimals;
  const totalUnits = included.reduce(
    (total, campaign) => total + Math.round(campaign.dailyBudget.effective * scale),
    0,
  );
  const allocations = included.map(
    (campaign) => byCampaign.get(campaign.ref.lineageKey)!.allocation,
  );
  let units: number[];
  if (allocations[0]?.basis === "amount") {
    units = allocations.map((allocation) => {
      if (allocation.basis !== "amount")
        return invalid("Budget allocations cannot mix amounts and percentages");
      validateNumeric(
        allocation.amount,
        capabilities.dailyBudget,
        "smart_creation.review_budget_invalid",
      );
      return Math.round(allocation.amount * scale);
    });
    if (units.reduce((total, amount) => total + amount, 0) !== totalUnits) {
      invalid("Campaign amounts must sum to the current total daily budget");
    }
  } else {
    const shares = allocations.map((allocation) => {
      if (allocation.basis !== "share")
        return invalid("Budget allocations cannot mix amounts and percentages");
      return allocation.basisPoints;
    });
    if (shares.reduce((total, share) => total + share, 0) !== 10_000)
      invalid("Campaign percentages must sum to 100%");
    const exact = shares.map((share) => (totalUnits * share) / 10_000);
    units = exact.map(Math.floor);
    const remainder = totalUnits - units.reduce((total, amount) => total + amount, 0);
    // Largest-remainder allocation conserves cents (or whole currency units) and
    // ties follow canonical Campaign order, not the order of the request.
    const ranked = exact
      .map((amount, index) => ({ index, fraction: amount - Math.floor(amount) }))
      .sort((left, right) => right.fraction - left.fraction || left.index - right.index);
    for (const { index } of ranked.slice(0, remainder)) units[index] = (units[index] ?? 0) + 1;
  }
  for (const [index, campaign] of included.entries()) {
    const amount = (units[index] ?? 0) / scale;
    validateNumeric(amount, capabilities.dailyBudget, "smart_creation.review_budget_invalid");
    campaign.dailyBudget = userValue(campaign.dailyBudget, amount);
    campaign.budgetAllocation = userValue(campaign.budgetAllocation, allocations[index]!);
  }
}

/** Select Precise automatic Campaigns together, then budget the final subset once. */
export function preparePreciseAutomaticSelection(
  workspace: SmartCreationReviewWorkspace,
  changes: readonly SmartCreationReviewChange[],
  capabilities: CampaignCreationCapabilities,
): Set<SmartCreationReviewChange> {
  const prepared = new Set<SmartCreationReviewChange>();
  if (workspace.draftContent.structure !== "precise") return prepared;
  const planTotal = workspace.graph.campaigns.reduce(
    (total, campaign) => total + (campaign.excluded.effective ? 0 : campaign.dailyBudget.effective),
    0,
  );
  const affectedCampaignKeys = new Set<string>();
  for (const change of changes) {
    if (change.operation !== "set_target_excluded") continue;
    const target = requireTarget(workspace.graph, change.ref);
    if (target.type !== "automatic") continue;
    target.excluded = userValue(target.excluded, change.excluded);
    const group = workspace.graph.adGroups.find(
      (item) => item.ref.lineageKey === target.adGroupLineageKey,
    );
    if (!group) throw new Error("Automatic Target Ad Group is missing");
    affectedCampaignKeys.add(group.campaignLineageKey);
    prepared.add(change);
  }
  if (!prepared.size) return prepared;
  for (const campaign of workspace.graph.campaigns) {
    if (!affectedCampaignKeys.has(campaign.ref.lineageKey)) continue;
    const excluded = workspace.graph.targets
      .filter((target) => campaign.adGroupLineageKeys.includes(target.adGroupLineageKey))
      .every((target) => target.excluded.effective);
    campaign.excluded = userValue(campaign.excluded, excluded);
  }
  const included = workspace.graph.campaigns.filter((campaign) => !campaign.excluded.effective);
  if (!included.length)
    throw new SmartCreationReviewError(
      422,
      "smart_creation.review_all_campaigns_excluded",
      "At least one Campaign must remain included",
    );
  const scale = 10 ** capabilities.dailyBudget.decimals;
  const amounts = allocateBoundedBudgetUnits(
    included.map((campaign) => Math.round(campaign.dailyBudget.effective * scale)),
    Math.round(planTotal * scale),
    Math.ceil(capabilities.dailyBudget.min * scale),
    Math.floor(capabilities.dailyBudget.max * scale),
  );
  if (!amounts)
    throw new SmartCreationReviewError(
      422,
      "smart_creation.review_budget_invalid",
      "The total daily budget cannot fit the selected Campaigns",
    );
  for (const [index, campaign] of included.entries()) {
    const amount = amounts[index]! / scale;
    campaign.dailyBudget = userValue(campaign.dailyBudget, amount);
    campaign.budgetAllocation = userValue(campaign.budgetAllocation, { basis: "amount", amount });
  }
  return prepared;
}
